import cn from 'classnames';
import * as AuthSelectors from 'domains/auth/auth.selectors';
import { PaymentProcessingStateEnum } from 'domains/payments/processing/interfaces.processing';
import * as paymentProcessingSelectors from 'domains/payments/processing/selectors.processing';
import * as productSelectors from 'domains/products/products.selectors';
import type { ProductsTypeV3 } from 'domains/products/products.types';
import * as purchasesSelector from 'domains/purchases/purchases.selectors';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useStore } from 'react-redux';

import type { AppState } from 'redux-store';

import styles from './styles.module.scss';

export interface ProductComponentAbstractProps {
  product: ProductsTypeV3;
  recommendProduct?: ProductsTypeV3;
  isSelected: boolean;
  nextPayment?: string;
  dailyPrice?: string;
  onClick: (productChoice: ProductsTypeV3) => Promise<void>;
}

export interface ProductsListProps<
  T extends ProductComponentAbstractProps,
  K = Omit<T, keyof ProductComponentAbstractProps>,
> {
  component: React.JSXElementConstructor<T>;
  onChange?: (product: ProductsTypeV3) => void;
  productsSelector?: (state: AppState) => ProductsTypeV3[];
  className?: string;
  extra?: K;
}

export function ProductsListAbstract<T extends ProductComponentAbstractProps>({
  component: Component,
  className,
  onChange,
  extra,
  productsSelector,
}: ProductsListProps<T>): JSX.Element {
  const store = useStore();
  const router = useRouter();
  const experimentOnlyBuyProduct = !!router.query.only_buy;
  const sale = !!router.query.sale;
  const dailyPrice = !!router.query.daily_price;

  const priceInterval = dailyPrice ? 'day' : undefined;

  let products = useSelector(
    productsSelector || productSelectors.getProductsSelector,
  );

  if (experimentOnlyBuyProduct) {
    products = products.filter(({ is_current }) => !is_current);
  }

  const paidProduct = useSelector(productSelectors.getPaidProductsSelector);
  const purchasePaid = useSelector((state: AppState) =>
    purchasesSelector.getPurchaseByProductIdSelector(
      state,
      paidProduct?.id || -1,
    ),
  );
  const productForPayment = useSelector(
    paymentProcessingSelectors.productSelector,
  );
  const isAuthenticated = useSelector(AuthSelectors.isAuthSelector);

  const selectedIdQuery = router.query.selected as string | undefined;
  const selectedProduct = selectedIdQuery
    ? products.find((product) => product.id === +selectedIdQuery)
    : undefined;
  // const selectedId = selectedIdQuery ? +selectedIdQuery : null;

  const defaultProduct =
    selectedIdQuery && selectedProduct?.id ||
    products.find((product) => !product.is_paid && product.prices[0].interval === 'year')
      ?.id ||
    products.find(
      (product) => !product.is_paid && product.prices[0].interval === 'lifetime',
    )?.id ||
    (products.filter(({ is_paid }) => !is_paid).length === 1
      ? products[0].id
      : null) ||
    null;

  const [productId, setProductId] = useState(defaultProduct);

  const product = useSelector((state: AppState) =>
    // @ts-ignore
    productSelectors.getProductSelector(state, productId),
  );
  
  const onClickHandler = useCallback(
    async (productChoice: ProductsTypeV3): Promise<void> => {
      const { id } = productChoice;
      setProductId(id);
      onChange?.(productChoice);
      (
        await import('domains/payments/processing')
      ).paymentsProcessingService.setProductById({ id, store });
    },
    [setProductId, store, onChange],
  );

  useEffect(() => {
    async function fn(): Promise<void> {
      if (!product || productForPayment) {
        return;
      }

      if (isAuthenticated && selectedProduct?.id) {
        (
          await import('domains/payments/processing')
        ).paymentsProcessingService.showModal({
          store,
          product,
          state: PaymentProcessingStateEnum.CARD,
        });

        return;
      }

      (
        await import('domains/payments/processing')
      ).paymentsProcessingService.showModal({ store, product });
    }

    fn();
  }, [product, store, isAuthenticated, selectedProduct?.id]);

  const render = products.map(item => (
    // @ts-ignore
    <Component
      key={item.id}
      product={item}
      isSelected={item.id === productId}
      nextPayment={purchasePaid?.next_pay}
      priceInterval={priceInterval}
      onClick={onClickHandler}
      className={styles.item}
      {...extra}
    />
  ));

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.content}>{render}</div>
    </div>
  );
}
